import React, { useState, useEffect } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Modal } from "../ui/Modal";
import apiService from "../../services/ApiService";
import {
  NetworkError,
  ApiError,
  type VerificatorMember,
} from "../../types/api";

export const VerificatorManagement: React.FC = () => {
  const [members, setMembers] = useState<VerificatorMember[]>([]);
  const [newMemberName, setNewMemberName] = useState("");
  const [editingMember, setEditingMember] = useState<VerificatorMember | null>(
    null
  );
  const [editName, setEditName] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] =
    useState<VerificatorMember | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(apiService.getNetworkStatus());

  // Load members on component mount and set up sync
  useEffect(() => {
    loadMembers();

    // Check network status periodically
    const intervalId = setInterval(() => {
      setIsOnline(apiService.getNetworkStatus());
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  const loadMembers = async () => {
    setIsLoading(true);
    setError("");

    try {
      if (!isOnline) {
        setError("No internet connection. Please check your network.");
        return;
      }

      if (!apiService.isAuthenticated()) {
        setError("Authentication required. Please log in again.");
        return;
      }

      const response = await apiService.manageMember({
        action: "list",
        params: { role: "verificator" },
      });

      console.log(response);

      if (Array.isArray(response)) {
        setMembers(response as VerificatorMember[]);
      } else {
        setError("Failed to load verificators");
      }
    } catch (error) {
      if (error instanceof NetworkError) {
        setError("Network error. Please check your connection and try again.");
      } else if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred");
        console.error("Load members error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!newMemberName.trim()) {
      setError("Please enter a verificator name");
      return;
    }

    if (!isOnline) {
      setError("No internet connection. Cannot add verificator.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await apiService.manageMember({
        action: "create",
        data: {
          full_name: newMemberName.trim(),
          role: "verificator",
        },
      });

      setNewMemberName("");
      await loadMembers(); // Refresh the list
    } catch (error) {
      if (error instanceof NetworkError) {
        setError("Network error. Please check your connection and try again.");
      } else if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred");
        console.error("Add member error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditMember = (member: VerificatorMember) => {
    setEditingMember(member);
    setEditName(member.full_name);
    setError("");
  };

  const handleUpdateMember = async () => {
    if (!editName.trim()) {
      setError("Please enter a verificator name");
      return;
    }

    if (!editingMember) return;

    if (!isOnline) {
      setError("No internet connection. Cannot update verificator.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await apiService.manageMember({
        action: "update",
        slug: `stch/${editingMember.id}`,
        data: {
          full_name: editName.trim(),
        },
      });

      setEditingMember(null);
      setEditName("");
      await loadMembers(); // Refresh the list
    } catch (error) {
      if (error instanceof NetworkError) {
        setError("Network error. Please check your connection and try again.");
      } else if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred");
        console.error("Update member error:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMember = (member: VerificatorMember) => {
    setShowDeleteConfirm(member);
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;

    if (!isOnline) {
      setError("No internet connection. Cannot delete verificator.");
      setShowDeleteConfirm(null);
      return;
    }

    setIsLoading(true);

    try {
      await apiService.manageMember({
        action: "delete",
        slug: `stch/${showDeleteConfirm.id}`,
      });

      await loadMembers(); // Refresh the list
    } catch (error) {
      if (error instanceof NetworkError) {
        setError("Network error. Please check your connection and try again.");
      } else if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred");
        console.error("Delete member error:", error);
      }
    } finally {
      setShowDeleteConfirm(null);
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Verificator Management
        </h3>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Error</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Verificator */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-md font-medium mb-2">Add New Verificator</h4>
        <div className="flex gap-2 items-start">
          <Input
            value={newMemberName}
            onChange={(e) => setNewMemberName(e.target.value)}
            placeholder="Enter verificator name"
            disabled={isLoading || !isOnline}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isLoading && isOnline) {
                handleAddMember();
              }
            }}
          />
          <Button
            variant="primary"
            onClick={handleAddMember}
            disabled={isLoading || !newMemberName.trim() || !isOnline}
          >
            {isLoading ? "Adding..." : "Add"}
          </Button>
        </div>
      </div>

      {/* Verificator List */}
      <div className="space-y-2">
        <h4 className="text-md font-medium">
          Verificator Members ({members.length})
        </h4>
        {members.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            {members.map((member) => (
              <div
                key={member.full_name}
                className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-gray-50"
              >
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {member.full_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    Added: {formatDate(member.created_at)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEditMember(member)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDeleteMember(member)}
                    className="text-red-600 hover:text-red-700"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            No verificators added yet. Add one to get started.
          </p>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingMember}
        onClose={() => {
          if (!isLoading) {
            setEditingMember(null);
            setError("");
          }
        }}
        title="Edit Verificator Name"
        showCloseButton={!isLoading}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setEditingMember(null);
                setError("");
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateMember}
              disabled={isLoading || !editName.trim() || !isOnline}
            >
              {isLoading ? "Updating..." : "Update"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="Enter verificator name"
            disabled={isLoading || !isOnline}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isLoading && isOnline) {
                handleUpdateMember();
              }
            }}
          />
          {!isOnline && (
            <p className="text-sm text-red-500">
              Internet connection required to update verificator.
            </p>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!showDeleteConfirm}
        onClose={() => !isLoading && setShowDeleteConfirm(null)}
        title="Confirm Delete"
        showCloseButton={!isLoading}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteConfirm(null)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmDelete}
              disabled={isLoading || !isOnline}
              className="bg-red-600 hover:bg-red-700"
            >
              {isLoading ? "Deleting..." : "Delete"}
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete verificator "
          <strong>{showDeleteConfirm?.full_name}</strong>"?
        </p>
        {showDeleteConfirm?.isActive && (
          <p className="text-sm text-orange-600 mt-2">
            Warning: This verificator has an active session.
          </p>
        )}
        <p className="text-sm text-gray-600 mt-2">
          This action cannot be undone, but historical data will be preserved.
        </p>
        {!isOnline && (
          <p className="text-sm text-red-600 mt-2">
            Internet connection required to delete verificator.
          </p>
        )}
      </Modal>
    </div>
  );
};
